import 'chartjs-plugin-zoom';
import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { CommonPipes } from 'app/trello/common-pipes.imports';
import { ReportRangeSelectorComponent } from 'app/trello/components/shared/report-range-selector/report-range-selector.component';
import { TotalsInsightPanelComponent } from 'app/trello/components/shared/visualisations/totals-insight-panel/totals-insight-panel.component';
import { BoardStages } from 'app/trello/models/board-stages';
import { BoardSummary, Statistics } from 'app/trello/models/board-summary';
import { DateRange } from 'app/trello/models/date-range';
import { Labels } from 'app/trello/models/labels';
import { Lists } from 'app/trello/models/lists';
import { Members } from 'app/trello/models/members';
import { BoardStagesFetcher } from 'app/trello/services/board-stages-fetcher';
import { BoardSummaryFetcher } from 'app/trello/services/board-summary-fetcher';
import { ComparableDateRangeProvider } from 'app/trello/services/comparable-date-range-provider';
import { LabelsFetcher } from 'app/trello/services/labels-fetcher';
import { ListsFetcher } from 'app/trello/services/lists-fetcher';
import { MembersFetcher } from 'app/trello/services/members-fetcher';
import { TrelloService } from 'app/trello/services/trello.service';
import { Templating } from 'app/trello/templating.imports';
import { StatisticsExtractor } from 'app/trello/utility/statistics-extractor';
import { ChartDataset, ChartOptions, ChartType } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { ProgressSpinner } from 'primeng/progressspinner';
import { combineLatest, Observable, switchMap, take } from 'rxjs';

@Component({
  imports: [
    CommonPipes,
    Templating,
    ProgressSpinner,
    ReportRangeSelectorComponent,
    TotalsInsightPanelComponent,
  ],
  selector: 'app-board-summary-component',
  styleUrls: ['./board-summary.component.css'],
  templateUrl: './board-summary.component.html',
})
export class BoardSummaryComponent implements OnInit {

  // ------------------------------------------------
  // Chart.js

  public chartJsOptions: ChartOptions = {};
  public chartLabels: string[] = [];
  public chartType: ChartType = 'bar';
  public chartLegend = true;
  public chartPlugins = [];
  public chartData: ChartDataset[] = [];

  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;

  // ------------------------------------------------

  protected dataLoaded = false;

  protected initialDateRange: DateRange | undefined;
  protected currentDateRange: DateRange | undefined;
  protected comparableDateRange: DateRange | undefined;

  protected boardStages: BoardStages | undefined;
  protected labels: Labels | undefined;
  protected lists: Lists | undefined;
  protected members: Members | undefined;
  protected stages = ['pending', 'inProgress', 'completed'];

  protected stats!: BoardSummary;
  protected comparableStats: BoardSummary | undefined;

  constructor(
    private readonly boardStagesFetcher: BoardStagesFetcher,
    private readonly boardSummaryStatisticsFetcher: BoardSummaryFetcher,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly comparableDateRangeProvider: ComparableDateRangeProvider,
    private readonly labelsFetcher: LabelsFetcher,
    private readonly listsFetcher: ListsFetcher,
    private readonly membersFetcher: MembersFetcher,
    private readonly statisticsExtractor: StatisticsExtractor,
    private readonly trelloService: TrelloService, // required
  ) {
  }

  ngOnInit(): void {

    this.chartJsOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: true,
          text: 'Trello Board Metrics',
        },
        tooltip: {
          mode: 'index',
          intersect: false,
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: 'Date',
          },
          ticks: {
            autoSkip: false,
          },
        },
        y: {
          type: 'linear',
          display: true,
          position: 'left',
          min: 75,
          max: 80,
          title: {
            display: true,
            text: 'Total Cards',
          },
          grid: {
            drawOnChartArea: true,
          },
        },
        y1: {
          type: 'linear',
          display: true,
          position: 'right',
          title: {
            display: true,
            text: 'Cards Created/Archived',
          },
          grid: {
            drawOnChartArea: false, // Only want the grid lines for one axis
          },
        },
      },
    };

    this.chartLabels = [
      '2024-12-06',
      '2024-12-07',
      '2024-12-08',
      '2024-12-09',
      '2024-12-10',
      '2024-12-11',
      '2024-12-12',
    ];

    this.chartData = [
      {
        label: 'Cards Created',
        type: 'bar',
        data: [2, 0, 0, 0, 0, 1, 0],
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        yAxisID: 'y1',
      },
      {
        label: 'Cards Archived',
        type: 'bar',
        data: [1, 5, 7, 0, 0, 0, 0],
        backgroundColor: 'rgba(255, 99, 132, 0.6)',
        yAxisID: 'y1',
      },
      {
        label: 'Total Cards',
        type: 'line',
        data: [77, 77, 77, 77, 77, 78, 78],
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        fill: false,
        yAxisID: 'y',
      },
    ];

    this.initialDateRange = {
      rangeType: 'lastDays7',
    };
  }

  protected statsForStage(
    stage: string,
  ): Statistics {
    return this.stats?.stages[stage];
  }

  protected comparableStatsForStage(
    stage: string,
  ): Statistics | undefined {
    return this.comparableStats?.stages[stage];
  }

  protected labelName(
    labelId: string,
  ): string {

    if (!this.labels) {
      return '';
    }

    const label =
      this.labels.labels
        .find(m => m.labelId === labelId);

    return label?.name || '[' + labelId + ']';
  }

  protected listName(
    listId: string,
  ): string {

    if (!this.lists) {
      return '';
    }

    const list =
      this.lists.lists
        .find(m => m.listId === listId);

    return list?.name || '[' + listId + ']';
  }

  protected memberName(
    memberId: string,
  ): string {

    if (!this.members) {
      return '';
    }

    const member =
      this.members.members
        .find(m => m.memberId === memberId);

    return member?.fullName || '[' + memberId + ']';
  }

  protected stageName(
    stage: string,
  ): string {

    switch (stage) {

      case 'pending':
        return 'Pending';

      case 'inProgress':
        return 'In Progress';

      case 'completed':
        return 'Completed';

      default:
        return '';
    }
  }

  protected some(
    obj: any,
  ): boolean {

    if (Array.isArray(obj)) {
      return obj.length > 0;

    } else if (obj && typeof obj === 'object') {
      return Object.keys(obj).length > 0;
    }

    return false;
  }

  protected onDateRangeSelected(
    dateRange: DateRange | undefined,
  ): void {

    this.currentDateRange = dateRange;

    this.loadStats();
  }

  private loadStats() {

    this.dataLoaded = false;

    if (!this.currentDateRange) {
      return;
    }

    const dateRange = this.currentDateRange;

    const from = dateRange.rangeFrom!;
    const to = dateRange.rangeTo!;
    const now = new Date().toISOString();

    this.comparableDateRangeProvider.getComparableDateRange(this.currentDateRange!)
      .pipe(
        take(1),
        switchMap((comparableDateRange) => {

          this.comparableDateRange = comparableDateRange;

          const dataFetches: Observable<any>[] = [
            this.boardStagesFetcher.fetch(),
            this.boardSummaryStatisticsFetcher.fetch(from, to, now),
            this.labelsFetcher.fetch(now),
            this.listsFetcher.fetch(now),
            this.membersFetcher.fetch(now),
          ];

          if (comparableDateRange) {

            const comparableFrom = comparableDateRange.rangeFrom!;
            const comparableTo = comparableDateRange.rangeTo!;

            dataFetches.push(this.boardSummaryStatisticsFetcher.fetch(comparableFrom, comparableTo, now));
          }

          return combineLatest(dataFetches);
        })
      )
      .subscribe({
        next: (results) => {

          let index = 0;

          this.boardStages = results[index++];
          this.stats = results[index++];
          this.labels = results[index++];
          this.lists = results[index++];
          this.members = results[index++];

          if (results.length > index) {
            this.comparableStats = results[index++];
          }

          this.dataLoaded = true;

          this.changeDetector.detectChanges();
        },
        error: (error) => {
          console.error('Error loading board summary stats:', error);
        }
      });
  }

}
